import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bell, Mail, MessageSquare, Loader2, Save } from "lucide-react";

const Toggle = ({ checked, onChange, label, hint, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="w-full flex items-start gap-4 p-4 rounded-2xl border border-[#EAE3D2] hover:border-[#0B5D3B] text-left transition-all"
    data-testid={testid}
  >
    <div className="flex-1">
      <div className="text-sm font-semibold text-[#111]">{label}</div>
      {hint && <div className="text-xs text-[#7A8A81] mt-0.5">{hint}</div>}
    </div>
    <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-[#0B5D3B]" : "bg-[#EAE3D2]"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
    </span>
  </button>
);

export default function NotificationPreferencesCard() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/notifications/preferences").then(r => setPrefs(r.data)).catch(() => setPrefs({}));
  }, []);

  const save = async () => {
    if (prefs.sms_opt_in && !prefs.sms_phone) {
      toast.error("Add a phone number to receive SMS");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.patch("/notifications/preferences", prefs);
      setPrefs(data);
      toast.success("Preferences saved");
    } catch { toast.error("Could not save"); }
    finally { setLoading(false); }
  };

  if (!prefs) return <div className="card-luxe p-6"><Loader2 className="animate-spin text-[#0B5D3B]" /></div>;

  return (
    <div className="card-luxe p-6" data-testid="notification-prefs-card">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-5 h-5 text-[#0B5D3B]" />
        <h3 className="font-serif text-xl">Notification Preferences</h3>
      </div>
      <p className="text-xs text-[#7A8A81] mb-5">Choose how we keep you in the loop about bookings, offers, and your membership.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="flex items-center gap-2 mb-2 md:col-span-2">
          <Mail className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#7A8A81]">Email channel</span>
        </div>
        <Toggle
          checked={prefs.email_opt_in}
          onChange={(v) => setPrefs({ ...prefs, email_opt_in: v })}
          label="Email updates"
          hint="Booking confirmations, receipts, and account alerts."
          testid="pref-email-opt-in"
        />

        <div className="flex items-center gap-2 mt-3 mb-2 md:col-span-2">
          <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#7A8A81]">SMS channel</span>
        </div>
        <Toggle
          checked={prefs.sms_opt_in}
          onChange={(v) => setPrefs({ ...prefs, sms_opt_in: v })}
          label="SMS text updates"
          hint="Booking confirmations + day-of reminders. Standard message rates may apply."
          testid="pref-sms-opt-in"
        />
        <div>
          <label className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#7A8A81] mb-1.5 block">SMS phone number</label>
          <input
            type="tel"
            value={prefs.sms_phone || ""}
            onChange={(e) => setPrefs({ ...prefs, sms_phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="w-full px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm"
            data-testid="pref-sms-phone"
          />
        </div>

        <div className="flex items-center gap-2 mt-3 mb-2 md:col-span-2">
          <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#7A8A81]">What to receive</span>
        </div>
        <Toggle
          checked={prefs.booking_reminders}
          onChange={(v) => setPrefs({ ...prefs, booking_reminders: v })}
          label="Booking reminders"
          hint="24-hour and same-day reminders for upcoming cleans."
          testid="pref-booking-reminders"
        />
        <Toggle
          checked={prefs.promotions}
          onChange={(v) => setPrefs({ ...prefs, promotions: v })}
          label="Promos & seasonal offers"
          hint="Discount codes, flash deals, holiday specials."
          testid="pref-promotions"
        />
        <Toggle
          checked={prefs.membership_updates}
          onChange={(v) => setPrefs({ ...prefs, membership_updates: v })}
          label="Membership updates"
          hint="Renewal reminders, perk releases, member-only events."
          testid="pref-membership-updates"
        />
      </div>

      <button onClick={save} disabled={loading} className="btn-primary text-sm py-2.5 px-5 disabled:opacity-60" data-testid="save-prefs">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save preferences</>}
      </button>

      <p className="text-[10px] text-[#7A8A81] mt-4">
        You can unsubscribe at any time. We never share your contact info.
      </p>
    </div>
  );
}
